// The feedback feature's rules, kept free of React and the DOM so they can be pinned
// down by feedbackRules.test.js: what a submission needs, the body each form posts,
// which entrants a piece of feedback might be about, and the Feedback list's
// filters. Used by the public form (pages/SubmitFeedback.jsx), the staff dialog
// (components/StaffFeedbackDialog.jsx) and the Feedback page.
//
// Imports nothing but constants.js, so it runs under Jest as-is.

export const emptyFeedbackForm = () => ({
  submitter_name: "",
  submitter_phone: "",
  submitter_email: "",
  on_behalf_of: "",
  consent: "",            // "" | "given" | "not_required"
  consent_reason: "",
  nature: [],
  event_id: "",           // an event id, OTHER_EVENT, or ""
  event_other: "",
  location: "",           // "loc:<id>", one of FEEDBACK_LOCATION_CHOICES, or ""
  location_details: "",
  details: "",
});

// The event picker's "it isn't listed" entry.
export const OTHER_EVENT = "other";

const blank = (v) => !String(v ?? "").trim();
const trimmed = (v) => (blank(v) ? null : String(v).trim());

// Loose on purpose: it catches a phone number typed in the wrong box, not every
// malformed address. The server sends to whatever it is given.
export const looksLikeEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

/**
 * What stops this form being sent, as sentences to show — empty when it may go.
 * Only the details are required everywhere; the public form also needs the event
 * (or a description of it), which a staff member's is fixed to.
 */
export const submissionProblems = (form, { staff = false } = {}) => {
  const problems = [];
  if (blank(form.details)) problems.push("Please tell us your feedback.");
  if (!staff) {
    if (blank(form.event_id)) problems.push("Please choose the event.");
    else if (form.event_id === OTHER_EVENT && blank(form.event_other)) {
      problems.push("Please tell us which event it was.");
    }
    if (!blank(form.submitter_email) && !looksLikeEmail(form.submitter_email)) {
      problems.push("That email address doesn't look right.");
    }
  }
  return problems;
};

// The parts both forms send: on whose behalf, with what consent, what kind, the words.
const sharedBody = (form) => {
  const onBehalf = trimmed(form.on_behalf_of);
  const consent = onBehalf && form.consent ? form.consent : null;
  return {
    on_behalf_of: onBehalf,
    consent,
    consent_reason: consent === "not_required" ? trimmed(form.consent_reason) : null,
    nature: form.nature || [],
    details: trimmed(form.details),
  };
};

/** The body POSTed to /public-feedback/. */
export const publicFeedbackBody = (form) => {
  const other = form.event_id === OTHER_EVENT;
  const loc = String(form.location || "");
  const locationId = loc.startsWith("loc:") ? Number(loc.slice(4)) : null;
  const choice = loc && !locationId ? loc : null;
  return {
    ...sharedBody(form),
    submitter_name: trimmed(form.submitter_name),
    submitter_phone: trimmed(form.submitter_phone),
    submitter_email: trimmed(form.submitter_email),
    event_id: other || blank(form.event_id) ? null : Number(form.event_id),
    event_other: other ? trimmed(form.event_other) : null,
    // A location belongs to a listed event; "Other" events have none to choose.
    event_location_id: other ? null : locationId,
    location_choice: choice,
    location_details: loc ? trimmed(form.location_details) : null,
  };
};

/** The body POSTed to /feedback/mine. Who, how to reach them and where come from the record. */
export const staffFeedbackBody = (form, eventId) => ({ ...sharedBody(form), event_id: eventId });

/** The events in a GET /feedback/mine response the person hasn't given feedback on yet. */
export const feedbackDue = (events) => (events || []).filter(e => !e.submitted);

// --- Linking feedback to an entrant ------------------------------------------

/** A name as it's compared: lower case, letters and digits only, single-spaced. */
export const nameKey = (s) =>
  String(s || "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ").trim();

/** Who the feedback is most likely about: the person it's on behalf of, else the sender. */
export const subjectName = (fb) => trimmed(fb?.on_behalf_of) || trimmed(fb?.submitter_name) || "";

/**
 * Entrants who might be the subject of this feedback. `exact` is a whole-name match
 * (a single one is offered for one-click confirmation); `partial` is every entrant
 * whose name has each word of the search as the start of one of its own words — so
 * "Jo Smith" finds "Joanne Smith" and "Smith" finds every Smith. A word of digits
 * also matches the race number.
 */
export const participantMatches = (query, participants) => {
  const q = nameKey(query);
  if (!q) return { exact: [], partial: [] };
  const words = q.split(" ");
  const exact = [];
  const partial = [];
  for (const p of participants || []) {
    const key = nameKey(p.name);
    if (key && key === q) { exact.push(p); continue; }
    const own = key ? key.split(" ") : [];
    const number = nameKey(p.race_number);
    const hit = words.every(w => own.some(o => o.startsWith(w)) || (number && w === number));
    if (hit) partial.push(p);
  }
  return { exact, partial };
};

// --- Showing a record --------------------------------------------------------

/** The event, as the record says it: the listed event, or what they typed instead. */
export const feedbackEventText = (fb) =>
  fb?.event_label || (fb?.event_other ? `Other: ${fb.event_other}` : "");

/** Where at the event: the location or choice, then any words they added. */
export const feedbackLocationText = (fb) =>
  [fb?.location_name || fb?.location_choice, fb?.location_details].filter(Boolean).join(" — ");

export const consentText = (fb) => {
  if (!fb?.on_behalf_of) return "";
  if (fb.consent === "given") return "Has their consent";
  if (fb.consent === "not_required") {
    return `Consent not required${fb.consent_reason ? `: ${fb.consent_reason}` : ""}`;
  }
  return "Consent not stated";
};

export const resolutionsText = (fb) =>
  (fb?.resolutions || [])
    .map(r => (r === "Other" && fb.resolution_other ? `Other: ${fb.resolution_other}` : r))
    .join(", ");

// --- The Feedback list's filters ---------------------------------------------

export const emptyFeedbackFilters = () => ({
  event: "",        // an event id, OTHER_EVENT for "not a listed event", or ""
  nature: "",
  status: "",
  resolution: "",   // a resolution, NO_RESOLUTION, or ""
  response: "",     // RESPONSE_OUTSTANDING, RESPONSE_OVERDUE, or ""
  search: "",
});

// --- The formal response a Complaint or Speaking Up is promised --------------

export const RESPONSE_OUTSTANDING = "outstanding";
export const RESPONSE_OVERDUE = "overdue";

/** Where a piece of feedback stands on its formal response: null when it needs none
 *  (the server sets `response_due_at` only for a Complaint or Speaking Up), else
 *  `{ due, state }` with state "closed" once it's Closed, "overdue" past the due
 *  date, or "due". Closed is taken to mean it has been responded to — nothing else
 *  records that. */
export const responseState = (fb, now = new Date()) => {
  if (!fb?.response_due_at) return null;
  const due = new Date(fb.response_due_at);
  if (fb.status === "Closed") return { due, state: "closed" };
  return { due, state: now > due ? "overdue" : "due" };
};

/** Whole days from `now` until `due` — negative once it's passed. */
export const daysUntil = (due, now = new Date()) => Math.ceil((due - now) / 86400000);

export const NO_RESOLUTION = "none";

/** The rows the filters leave. `search` reads the sender, the subject and the linked entrant. */
export const filterFeedback = (rows, f, now = new Date()) => {
  const q = nameKey(f.search);
  return (rows || []).filter(fb => {
    if (f.response) {
      const state = responseState(fb, now)?.state;
      if (f.response === RESPONSE_OVERDUE ? state !== "overdue"
        : state !== "due" && state !== "overdue") return false;
    }
    if (f.event === OTHER_EVENT) { if (fb.event_id != null) return false; }
    else if (f.event && String(fb.event_id) !== String(f.event)) return false;
    if (f.nature && !(fb.nature || []).includes(f.nature)) return false;
    if (f.status && fb.status !== f.status) return false;
    if (f.resolution === NO_RESOLUTION) { if ((fb.resolutions || []).length) return false; }
    else if (f.resolution && !(fb.resolutions || []).includes(f.resolution)) return false;
    if (q) {
      const hay = [fb.submitter_name, fb.on_behalf_of, fb.participant_label].map(nameKey).join(" | ");
      if (!hay.includes(q)) return false;
    }
    return true;
  });
};

// --- Staff reading and changing their own feedback ---------------------------

/** A submitted piece of staff feedback (GET /feedback/mine/submitted) back in form state. */
export const feedbackToForm = (fb) => ({
  ...emptyFeedbackForm(),
  on_behalf_of: fb.on_behalf_of || "",
  consent: fb.consent || "",
  consent_reason: fb.consent_reason || "",
  nature: fb.nature || [],
  details: fb.details || "",
});

/** Whether the form says anything different from what was submitted. */
export const feedbackEdited = (fb, form) => {
  const was = staffFeedbackBody(feedbackToForm(fb), fb.event_id);
  const now = staffFeedbackBody(form, fb.event_id);
  return JSON.stringify(was) !== JSON.stringify(now);
};

/** What stops an edit being saved: the usual rules, a change, and a reason for it. */
export const staffEditProblems = (fb, form, whatChanged) => {
  const problems = submissionProblems(form, { staff: true });
  if (!feedbackEdited(fb, form)) problems.push("Nothing has changed yet.");
  if (blank(whatChanged)) problems.push("Please say what you changed.");
  return problems;
};

/** The body PUT to /feedback/mine/{id}. */
export const staffEditBody = (fb, form, whatChanged) => ({
  ...staffFeedbackBody(form, fb.event_id),
  what_changed: trimmed(whatChanged),
});

/** The person's feedback by event id — one each — for My events' past events. */
export const feedbackByEvent = (items) => {
  const out = new Map();
  for (const fb of items || []) if (!out.has(fb.event_id)) out.set(fb.event_id, fb);
  return out;
};
